import React, { Dispatch, ReactNode, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from '@kite-js/core';
import { SignUpTypes } from '../constants/common/signUpType';
import { CmsIds } from '../constants/common/CmsIds';
import {
    PartnerTypeChoice,
    PartnerTypeSelectOption,
    buildPartnerTypeItems,
    isPartnerTypeChoice,
} from '../constants/common/partnerTypeChoice';
import {
    AFFILIATE_AGREEMENT_PDF,
    AMBASSADOR_AGREEMENT_PDF,
    CORPORATE_AGREEMENT_PDF,
} from '../constants/common/agreementUrls';
import { formatMessageJsx } from '../utils/formatMessage';
import { parseSignUpType } from '../utils/getSignUpType';

interface UsePartnerTypeSelectionOptions {
    onPartnerTypeChange?: (next: PartnerTypeChoice) => void;
}

interface UsePartnerTypeSelectionResult {
    hasPsrcInQuery: boolean;
    signUpTypeFromUrl: SignUpTypes;
    partnerType: PartnerTypeChoice | null;
    setPartnerType: Dispatch<SetStateAction<PartnerTypeChoice | null>>;
    partnerTypeItems: PartnerTypeSelectOption[];
    partnerTypeSelectValue: PartnerTypeSelectOption | undefined;
    handlePartnerTypeSelectChange: (selected: PartnerTypeSelectOption[]) => void;
    agreementLabelContent: ReactNode;
}

export const getAutoSelectPartnerType = (
    affType: string | null,
    hasPsrcInQuery: boolean,
): PartnerTypeChoice | null => {
    if (!affType) return null;
    if (!/^\d+$/.test(affType)) return null;
    const signupTypeQS = Number(affType);
    if (!isPartnerTypeChoice(signupTypeQS)) return null;
    if (signupTypeQS === SignUpTypes.Ambassador && hasPsrcInQuery) return null;
    return signupTypeQS;
};

export const usePartnerTypeSelection = (
    cmsItems: Cms.CmsDictionary,
    options: UsePartnerTypeSelectionOptions = {},
): UsePartnerTypeSelectionResult => {
    const { onPartnerTypeChange } = options;
    const [urlSearchParams] = useSearchParams();
    const hasPsrcInQuery = urlSearchParams.has('psrc');
    const signUpTypeFromUrl = parseSignUpType(urlSearchParams.get('psrc'));

    const [partnerType, setPartnerType] = useState<PartnerTypeChoice | null>(() =>
        getAutoSelectPartnerType(urlSearchParams.get('affType'), hasPsrcInQuery),
    );

    const partnerTypeItems = useMemo(() => {
        const items = buildPartnerTypeItems(cmsItems);
        if (hasPsrcInQuery) {
            return items.filter((item) => item.value !== String(SignUpTypes.Ambassador));
        }
        return items;
    }, [cmsItems, hasPsrcInQuery]);

    const partnerTypeSelectValue = useMemo(
        () => (partnerType === null
            ? undefined
            : partnerTypeItems.find((item) => item.value === partnerType.toString())),
        [partnerType, partnerTypeItems],
    );

    const handlePartnerTypeSelectChange = useCallback(
        (selected: PartnerTypeSelectOption[]) => {
            if (!selected?.length || !selected[0]) {
                setPartnerType(null);
                return;
            }
            const n = Number(selected[0].value);
            if (isPartnerTypeChoice(n)) {
                setPartnerType((prev) => {
                    if (prev !== n) {
                        onPartnerTypeChange?.(n);
                    }
                    return n;
                });
            }
        },
        [onPartnerTypeChange],
    );

    const agreementLabelContent = useMemo<ReactNode>(() => {
        if (partnerType === null) {
            return cmsItems[CmsIds.AgreementPlaceholder];
        }

        const affiliateAgreementUrl =
            signUpTypeFromUrl === SignUpTypes.Corporate
                ? CORPORATE_AGREEMENT_PDF
                : AFFILIATE_AGREEMENT_PDF;
        const agreementUrl =
            partnerType === SignUpTypes.Affiliate
                ? affiliateAgreementUrl
                : AMBASSADOR_AGREEMENT_PDF;
        const linkText =
            partnerType === SignUpTypes.Affiliate
                ? cmsItems[CmsIds.AffiliateAgrement]
                : cmsItems[CmsIds.AmbassadorAgreement];

        return formatMessageJsx(cmsItems[CmsIds.AgreeToTermsAndCond], [
            <Link key="agreement" href={agreementUrl} target="_blank" rel="noopener noreferrer">
                {linkText}
            </Link>,
        ]);
    }, [cmsItems, partnerType, signUpTypeFromUrl]);

    return {
        hasPsrcInQuery,
        signUpTypeFromUrl,
        partnerType,
        setPartnerType,
        partnerTypeItems,
        partnerTypeSelectValue,
        handlePartnerTypeSelectChange,
        agreementLabelContent,
    };
};
